import axiosInstance from '../api';

export type ApprovalRequirement = 'NONE' | 'USER' | 'AGENT' | 'BROKER';
export type EscalationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
export type AgentType = 'SEARCH' | 'SHOWING' | 'OFFER' | 'TRANSACTION' | 'COMMUNICATION' | 'DOCUMENT' | 'BUYER' | 'MARKETING' | 'ORCHESTRATOR';
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface PendingApproval {
  id: string;
  workflowId?: string;
  taskId?: string;
  userId?: string;
  agentType: AgentType;
  action: string;
  description: string;
  context: Record<string, any>;
  /**
   * True if approving this action sends (or queues) an external comm — directly or via a
   * downstream subtask. For a data update this follows the card's own outbound preview
   * (`context.outboundComms`), which says who is told and whether any of it leaves Homepoint.
   */
  createsExternalComms: boolean;
  requiresApproval: ApprovalRequirement;
  suggestedResponse?: string;
  status: EscalationStatus;
  priority: TaskPriority;
  expiresAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;

  // Included relations
  workflow?: {
    id: string;
    type: string;
    currentState: string;
    status: string;
  };
  task?: {
    id: string;
    action: string;
    status: string;
  };
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
}

export interface PendingApprovalQuery {
  status?: EscalationStatus | EscalationStatus[];
  agentType?: AgentType | AgentType[];
  requiresApproval?: ApprovalRequirement | ApprovalRequirement[];
  priority?: TaskPriority | TaskPriority[];
  userId?: string;
  workflowId?: string;
  $limit?: number;
  $skip?: number;
  $sort?: Record<string, 1 | -1>;
}

export interface PaginatedApprovals {
  total: number;
  limit: number;
  skip: number;
  data: PendingApproval[];
}

export interface ApprovalDecision {
  status: 'APPROVED' | 'REJECTED';
  note?: string;
  modifiedParams?: Record<string, any>;
}

const pendingApprovalsService = {
  find: async (query?: PendingApprovalQuery): Promise<PaginatedApprovals> => {
    const response = await axiosInstance.get<PaginatedApprovals>('/agent-coordinator/approvals', { params: query });
    return response.data;
  },

  get: async (id: string): Promise<PendingApproval> => {
    const response = await axiosInstance.get<PendingApproval>(`/agent-coordinator/approvals/${id}`);
    return response.data;
  },

  approve: async (id: string, decision: ApprovalDecision): Promise<PendingApproval> => {
    const response = await axiosInstance.patch<PendingApproval>(
      `/agent-coordinator/approvals/${id}`,
      decision
    );
    return response.data;
  },

  reject: async (id: string, reason: string): Promise<PendingApproval> => {
    const response = await axiosInstance.patch<PendingApproval>(
      `/agent-coordinator/approvals/${id}`,
      { status: 'REJECTED', note: reason }
    );
    return response.data;
  },

  // Helper methods
  getPending: async (): Promise<PaginatedApprovals> => {
    return pendingApprovalsService.find({
      status: 'PENDING',
      $sort: { priority: -1, createdAt: -1 }
    });
  },

  getUrgent: async (): Promise<PaginatedApprovals> => {
    return pendingApprovalsService.find({
      status: 'PENDING',
      priority: ['URGENT', 'HIGH'],
      $sort: { priority: -1, createdAt: -1 }
    });
  },

  getByWorkflow: async (workflowId: string): Promise<PaginatedApprovals> => {
    return pendingApprovalsService.find({
      workflowId,
      $sort: { createdAt: -1 }
    });
  },

  getExpiringSoon: async (hoursThreshold: number = 24): Promise<PaginatedApprovals> => {
    const expiryDate = new Date();
    expiryDate.setHours(expiryDate.getHours() + hoursThreshold);
    return pendingApprovalsService.find({
      status: 'PENDING',
      $sort: { expiresAt: 1 }
    });
  }
};

export default pendingApprovalsService;
